
import { AxiosResponse } from 'axios';
import _ from 'underscore';
import {
  computed, defineComponent, onMounted, ref, watch,
} from 'vue';

import bannerApi from '~api/banner';
import {
  ApiBanner,
  ApiBannerLink,
} from '~model';
import store from '~store';

export default defineComponent({
  props: {
  /* Voucher category ID as number. Like 1 for default Wunschgutschein or 12 on /kids variation */
    voucherCategoryId: {
      type: Number,
      required: true,
    },
    /* Filter flag as a string like "showInVault", "showOnShopWall" or "showOnStartPage" */
    filterFlag: {
      type: String,
      required: true,
    },
    onPage: {
      type: String,
      default: '',
      validator: (value: string) => ['redeem', 'shoplist', 'thankyou'].includes(value),
    },
  },

  setup(props) {
    const getters = store?.getters;

    const form = store.state.customer?.form;
    const banners = ref([] as ApiBanner[]);
    const banner = ref(null as unknown as ApiBanner);

    const getLocale = computed(() => getters.getLocale);
    const showApiBanner = computed(() => getters.getShowApiBanner);

    /*
     * Link of Banner
     */
    const link = computed((): ApiBannerLink => banner.value.link);

    /*
     * Link Type of Banner
     */
    const linkType = computed(() => banner.value.linkType);

    /*
     * Banner has a static/external link
     */
    const hasStaticLink = computed(() => linkType.value === 'static');

    /*
     * Banner has a VueRouter Shop link.
     */
    const hasShopLink = computed(() => linkType.value === 'shop');

    /*
     * Banner has no link at all
     */
    const hasNoLink = computed(() => linkType.value === 'none');

    /*
     * The VouRouter shop-link (to-attribute)
     */
    const shopLink = computed(() => ({
      name: 'shopDetails',
      params: {
        id: link.value,
      },
    }));

    /*
     * The external link (href-attribute)
     */
    const staticLink = computed(() => banner.value?.link);

    function fetchBanner() {
      if (showApiBanner.value && getters?.getVoucherPartner !== 'ssc') {
        bannerApi
          .getByVoucherCategoryId(props.voucherCategoryId, {
            distribution: getters?.getVoucherDistribution,
            voucherValue: getters?.getVoucherValue,
          })
          .then((response: AxiosResponse) => {
            banners.value = response?.data;
            // eslint-disable-next-line no-use-before-define
            banner.value = getRandomBanner() as ApiBanner;
          });
      }
    }

    watch(getLocale, () => {
      fetchBanner();
    });

    onMounted(() => {
      // Quickfix. No banner on SSC
      fetchBanner();
    });

    /*
     * Filter fetched banners by the string-property "filterFlag".
     * Pick a random sample from array of banners and return the banner as an object.
     */
    function getRandomBanner(): ApiBanner | undefined {
      if (banners.value.length > 0) {
        return _.chain(banners.value)
          .filter((item) => item?.flags?.includes(props.filterFlag))
          .sample()
          .value();
      }
      return undefined;
    }

    /*
     * Get the CSS-class for the inner banner image as string
     */
    function getClass(key: string): string[] {
      const cssClasses = ['m-auto'];
      switch (key) {
        case 'desktop':
          return [...cssClasses, 'd-none', 'd-md-block', 'd-lg-block'];
        case 'mobile':
          return [...cssClasses, 'd-block', 'd-md-none'];
        default:
          return cssClasses;
      }
    }

    // When the user clicked on the banner.
    function onClick(event: Event): void {
      const linkString = String(staticLink.value);

      if (props.onPage === 'shoplist') {
        form.redeemStartingPoint = { source: 'banner', campaignName: banner.value.name };
      }

      switch (true) {
        case /^\/?(\?)filters=%7B/i.test(linkString):
          window.history.pushState({}, '', linkString);
          store.dispatch('resetFilters');
          store.dispatch('loadFiltersFromUrlGetParameters');
          event.preventDefault();
          break;

          // no default
      }
    }

    return {
      shopLink,
      banner,
      banners,
      staticLink,

      getClass,
      onClick,

      hasStaticLink,
      hasShopLink,
      hasNoLink,
    };
  },
});
